import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ticketsAPI, usersAPI, paymentsAPI, subscriptionAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { MessageSquare, Plus, Search, Send, MoreVertical, Clock, CheckCircle2, AlertCircle, XCircle, ArrowLeft, UserCircle, Loader2 } from 'lucide-react';

const STATUS_MAP = {
  open: { label: 'Aperto', color: 'bg-yellow-500/15 text-yellow-500 border-yellow-500/20 border', icon: Clock },
  in_progress: { label: 'In Corso', color: 'bg-blue-500/15 text-blue-500 border-blue-500/20 border', icon: AlertCircle },
  resolved: { label: 'Risolto', color: 'bg-green-500/15 text-green-500 border-green-500/20 border', icon: CheckCircle2 },
  closed: { label: 'Chiuso', color: 'bg-muted-foreground/15 text-muted-foreground border-muted-foreground/20 border', icon: XCircle },
};

const PRIORITY_MAP = {
  low: { label: 'Bassa', color: 'text-green-500' },
  medium: { label: 'Media', color: 'text-yellow-500' },
  high: { label: 'Alta', color: 'text-red-500' },
};

export default function TicketsPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === 'root' || user?.role === 'administrator';
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersStats, setUsersStats] = useState([]);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [createForm, setCreateForm] = useState({ subject: '', description: '', priority: 'medium', for_user_id: '' });
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [ticketUsage, setTicketUsage] = useState(null);
  const [showPurchase, setShowPurchase] = useState(false);
  const [purchaseQty, setPurchaseQty] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [ticketPrice, setTicketPrice] = useState(5); // Default price

  useEffect(() => { loadTickets(); loadTicketPrice(); }, []);
  
  // Handle Stripe return for ticket purchase
  useEffect(() => {
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');
    
    if (success === 'true' && sessionId) {
      handlePaymentSuccess(sessionId);
      setSearchParams({});
    } else if (canceled === 'true') {
      toast.error('Acquisto annullato');
      setSearchParams({});
    }
  }, [searchParams]);

  const handlePaymentSuccess = async (sessionId) => {
    setVerifyingPayment(true);
    try {
      for (let i = 0; i < 10; i++) {
        const res = await paymentsAPI.getSessionStatus(sessionId);
        if (res.data.status === 'completed') {
          toast.success('Ticket extra acquistati con successo!');
          await checkAuth();
          await loadTickets();
          break;
        }
        await new Promise(r => setTimeout(r, 1000));
      }
    } catch {
      toast.error('Errore nella verifica del pagamento');
    } finally {
      setVerifyingPayment(false);
    }
  };

  const loadTicketPrice = async () => {
    try {
      const res = await subscriptionAPI.getPublicConfig();
      const price = res.data?.extra_ticket_price;
      if (price !== undefined && price !== null) {
        setTicketPrice(price);
      }
    } catch (err) {
      console.log('Error loading ticket price, using default:', err);
    }
  };

  const loadTickets = async () => {
    try {
      const promises = [ticketsAPI.getAll()];
      if (!isAdmin) {
        promises.push(ticketsAPI.getUsage());
      } else {
        promises.push(usersAPI.getAll());
        promises.push(ticketsAPI.getUsersStats());
      }
      const results = await Promise.all(promises);
      setTickets(results[0].data);
      if (!isAdmin && results[1]) {
        setTicketUsage(results[1].data);
      } else if (isAdmin) {
        setUsers(results[1].data);
        if (results[2]) setUsersStats(results[2].data);
      }
    } catch {
      toast.error('Errore nel caricamento ticket');
    } finally {
      setLoading(false);
    }
  };

  // Get user stats for dropdown
  const getUserStats = (userId) => {
    return usersStats.find(s => s.user_id === userId);
  };

  const handlePurchaseExtra = async () => {
    setPurchasing(true);
    try {
      const originUrl = window.location.origin;
      const res = await paymentsAPI.createTicketsCheckout(purchaseQty, originUrl);
      // Redirect to Stripe Checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.startsWith('UPGRADE_REQUIRED:')) {
        toast.error(detail.replace('UPGRADE_REQUIRED:', ''));
        setShowPurchase(false);
        navigate('/subscription');
      } else if (detail.includes('Stripe non configurato')) {
        toast.error('Stripe non configurato. Contatta l\'amministratore.');
      } else {
        toast.error(detail || 'Errore acquisto');
      }
      setPurchasing(false);
    }
  };

  const filteredTickets = tickets.filter(t => {
    const matchSearch = t.subject?.toLowerCase().includes(search.toLowerCase()) ||
      t.user_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || t.status === filterStatus;
    const matchUser = !isAdmin || filterUser === 'all' || t.user_id === filterUser;
    return matchSearch && matchStatus && matchUser;
  });

  // Get unique users from tickets for filter dropdown
  const ticketUsers = isAdmin ? [...new Map(tickets.map(t => [t.user_id, { user_id: t.user_id, user_name: t.user_name }])).values()] : [];

  const handleCreate = async () => {
    if (!createForm.subject || !createForm.description) {
      toast.error('Compila tutti i campi');
      return;
    }
    if (isAdmin && !createForm.for_user_id) {
      toast.error('Seleziona un utente per il ticket');
      return;
    }
    try {
      const payload = {
        subject: createForm.subject,
        description: createForm.description,
        priority: createForm.priority
      };
      // Add for_user_id only if admin selected a user
      if (isAdmin && createForm.for_user_id) {
        payload.for_user_id = createForm.for_user_id;
      }
      const res = await ticketsAPI.create(payload);
      toast.success('Ticket creato');
      setShowCreate(false);
      setCreateForm({ subject: '', description: '', priority: 'medium', for_user_id: '' });
      loadTickets();
      setSelectedTicket(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.startsWith('UPGRADE_REQUIRED:')) {
        toast.error(detail.replace('UPGRADE_REQUIRED:', ''));
        setShowCreate(false);
        navigate('/subscription');
      } else {
        toast.error(detail || 'Errore creazione ticket');
      }
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const res = await ticketsAPI.addMessage(selectedTicket.ticket_id, newMessage.trim());
      setSelectedTicket(res.data);
      setNewMessage('');
      loadTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio messaggio');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (ticketId, status) => {
    try {
      const res = await ticketsAPI.updateStatus(ticketId, status);
      if (selectedTicket?.ticket_id === ticketId) setSelectedTicket(res.data);
      toast.success(`Stato aggiornato: ${STATUS_MAP[status]?.label}`);
      loadTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento stato');
    }
  };

  const handleDelete = async (ticketId) => {
    try {
      await ticketsAPI.delete(ticketId);
      toast.success('Ticket eliminato');
      if (selectedTicket?.ticket_id === ticketId) setSelectedTicket(null);
      loadTickets();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  // Ticket detail view
  if (selectedTicket) {
    const status = STATUS_MAP[selectedTicket.status] || STATUS_MAP.open;
    const StatusIcon = status.icon;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setSelectedTicket(null)} data-testid="ticket-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="ticket-detail-subject">
              {selectedTicket.subject}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={status.color}><StatusIcon className="w-3 h-3 mr-1" />{status.label}</Badge>
              <Badge variant="outline" className={PRIORITY_MAP[selectedTicket.priority]?.color}>
                {PRIORITY_MAP[selectedTicket.priority]?.label}
              </Badge>
              {isAdmin && <span className="text-xs text-muted-foreground">di {selectedTicket.user_name} ({selectedTicket.user_email})</span>}
            </div>
          </div>
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="ticket-status-dropdown">
                  Cambia Stato
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover border-border">
                {Object.entries(STATUS_MAP).map(([key, val]) => (
                  <DropdownMenuItem key={key} onClick={() => handleStatusChange(selectedTicket.ticket_id, key)} className="cursor-pointer">
                    <val.icon className="w-4 h-4 mr-2" /> {val.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <Card className="border-border bg-card flex flex-col" style={{ height: 'calc(100vh - 16rem)' }}>
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {(selectedTicket.messages || []).map((msg) => {
                const isMe = msg.from_user_id === user?.user_id;
                const isStaff = msg.from_role === 'root' || msg.from_role === 'administrator';
                return (
                  <div key={msg.msg_id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-lg p-3 ${
                      isStaff && !isMe
                        ? 'bg-primary/10 border border-primary/20'
                        : isMe
                          ? 'bg-secondary border border-border'
                          : 'bg-muted border border-border'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold">
                          {isMe ? 'Tu' : msg.from_name}
                        </span>
                        {isStaff && !isMe && (
                          <Badge variant="outline" className="text-[10px] py-0 px-1 text-primary border-primary/30">Staff</Badge>
                        )}
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(msg.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {selectedTicket.status !== 'closed' && (
            <div className="p-4 border-t border-border">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Scrivi un messaggio..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                  data-testid="ticket-message-input"
                  className="min-h-[60px] max-h-[120px]"
                  rows={2}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={sending || !newMessage.trim()}
                  data-testid="ticket-send-btn"
                  className="bg-primary text-primary-foreground self-end"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          {selectedTicket.status === 'closed' && (
            <div className="p-4 border-t border-border text-center text-sm text-muted-foreground">
              Questo ticket e' chiuso. Non e' possibile aggiungere messaggi.
            </div>
          )}
        </Card>
      </div>
    );
  }

  // Ticket list view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="tickets-page-title">
            Ticket Supporto
          </h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin ? 'Gestisci i ticket di supporto' : 'Crea e gestisci le tue richieste di supporto'}
          </p>
        </div>
        <div className="flex gap-2">
          {!isAdmin && (
            <Button
              variant="outline"
              data-testid="purchase-extra-tickets-btn"
              onClick={() => setShowPurchase(true)}
            >
              <Plus className="w-4 h-4 mr-2" /> Ticket Extra
            </Button>
          )}
          <Button
            data-testid="create-ticket-btn"
            onClick={() => setShowCreate(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4 mr-2" /> {isAdmin ? 'Apri Ticket per Utente' : 'Nuovo Ticket'}
          </Button>
        </div>
      </div>

      {/* Ticket Usage Banner */}
      {!isAdmin && ticketUsage && (
        <Card className="border-border bg-card">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm font-medium">
                  Ticket utilizzati: <span className="text-primary" data-testid="ticket-usage-count">{ticketUsage.used_tickets}</span>
                  {ticketUsage.total_allowed !== null ? ` / ${ticketUsage.total_allowed}` : ' (illimitati)'}
                  {ticketUsage.extra_tickets > 0 && (
                    <span className="text-xs text-muted-foreground ml-1">(di cui {ticketUsage.extra_tickets} extra)</span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  Periodo: {ticketUsage.period_start ? new Date(ticketUsage.period_start).toLocaleDateString('it-IT') : '-'}
                  {' - '}
                  {ticketUsage.period_end ? new Date(ticketUsage.period_end).toLocaleDateString('it-IT') : '-'}
                </p>
              </div>
            </div>
            {ticketUsage.total_allowed !== null && ticketUsage.remaining_tickets !== null && (
              <Badge
                variant="outline"
                className={ticketUsage.remaining_tickets === 0 ? 'text-red-500 border-red-500/30' : 'text-green-500 border-green-500/30'}
                data-testid="ticket-remaining-badge"
              >
                {ticketUsage.remaining_tickets === 0 ? 'Limite raggiunto' : `${ticketUsage.remaining_tickets} rimanenti`}
              </Badge>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36" data-testid="filter-ticket-status">
                  <SelectValue placeholder="Filtro stato" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="open">Aperti</SelectItem>
                  <SelectItem value="in_progress">In Corso</SelectItem>
                  <SelectItem value="resolved">Risolti</SelectItem>
                  <SelectItem value="closed">Chiusi</SelectItem>
                </SelectContent>
              </Select>
              {isAdmin && ticketUsers.length > 0 && (
                <Select value={filterUser} onValueChange={setFilterUser}>
                  <SelectTrigger className="w-48" data-testid="filter-ticket-user">
                    <SelectValue placeholder="Filtra utente" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border max-h-60">
                    <SelectItem value="all">Tutti gli utenti</SelectItem>
                    {ticketUsers.map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.user_name || u.user_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Cerca ticket..." data-testid="search-tickets" className="pl-10" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {filteredTickets.map((ticket) => {
              const st = STATUS_MAP[ticket.status] || STATUS_MAP.open;
              const StIcon = st.icon;
              const lastMsg = ticket.messages?.[ticket.messages.length - 1];
              return (
                <div
                  key={ticket.ticket_id}
                  data-testid={`ticket-row-${ticket.ticket_id}`}
                  className="p-4 hover:bg-accent/50 transition-colors cursor-pointer flex items-center gap-4"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <StIcon className={`w-5 h-5 ${st.color.includes('yellow') ? 'text-yellow-500' : st.color.includes('blue') ? 'text-blue-500' : st.color.includes('green') ? 'text-green-500' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-medium text-sm truncate">{ticket.subject}</span>
                      <Badge className={`text-[10px] ${st.color}`}>{st.label}</Badge>
                      <Badge variant="outline" className={`text-[10px] ${PRIORITY_MAP[ticket.priority]?.color}`}>
                        {PRIORITY_MAP[ticket.priority]?.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {isAdmin && <span className="font-medium">{ticket.user_name} - </span>}
                      {lastMsg?.text?.substring(0, 80)}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-muted-foreground">
                      {new Date(ticket.updated_at).toLocaleDateString('it-IT')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {ticket.messages?.length || 0} msg
                    </p>
                  </div>
                  {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="flex-shrink-0" data-testid={`ticket-actions-${ticket.ticket_id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-popover border-border">
                        {Object.entries(STATUS_MAP).map(([key, val]) => (
                          <DropdownMenuItem key={key} onClick={(e) => { e.stopPropagation(); handleStatusChange(ticket.ticket_id, key); }} className="cursor-pointer">
                            <val.icon className="w-4 h-4 mr-2" /> {val.label}
                          </DropdownMenuItem>
                        ))}
                        <Separator className="my-1" />
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(ticket.ticket_id); }} className="cursor-pointer text-destructive focus:text-destructive">
                          <XCircle className="w-4 h-4 mr-2" /> Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              );
            })}
            {filteredTickets.length === 0 && (
              <div className="p-8 text-center text-muted-foreground">
                {loading ? 'Caricamento...' : 'Nessun ticket trovato'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Nuovo Ticket di Supporto</DialogTitle>
            <DialogDescription>
              {isAdmin ? "Crea un ticket per un utente" : "Descrivi il tuo problema e il nostro team ti rispondera'"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {isAdmin && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <UserCircle className="w-4 h-4" /> Per l'Utente *
                </Label>
                <Select value={createForm.for_user_id} onValueChange={(v) => setCreateForm({ ...createForm, for_user_id: v })}>
                  <SelectTrigger data-testid="ticket-user-select">
                    <SelectValue placeholder="Seleziona un utente" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border max-h-60">
                    {users.filter(u => u.role !== 'root' && u.role !== 'administrator').map(u => {
                      const stats = getUserStats(u.user_id);
                      return (
                        <SelectItem key={u.user_id} value={u.user_id}>
                          <div className="flex items-center justify-between w-full gap-4">
                            <span>{u.name} ({u.email})</span>
                            {stats && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                stats.remaining_tickets === 0 
                                  ? 'bg-red-500/20 text-red-400' 
                                  : stats.remaining_tickets <= 2 
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : 'bg-green-500/20 text-green-400'
                              }`}>
                                {stats.remaining_tickets !== null ? `${stats.remaining_tickets} disp.` : 'Illimitati'}
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {createForm.for_user_id && getUserStats(createForm.for_user_id) && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Ticket: {getUserStats(createForm.for_user_id).used_tickets} usati / {getUserStats(createForm.for_user_id).total_allowed || '∞'} totali
                    {getUserStats(createForm.for_user_id).extra_tickets > 0 && ` (${getUserStats(createForm.for_user_id).extra_tickets} extra)`}
                  </p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Oggetto *</Label>
              <Input
                data-testid="ticket-subject-input"
                placeholder="Breve descrizione del problema"
                value={createForm.subject}
                onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Priorita'</Label>
              <Select value={createForm.priority} onValueChange={(v) => setCreateForm({ ...createForm, priority: v })}>
                <SelectTrigger data-testid="ticket-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="low">Bassa</SelectItem>
                  <SelectItem value="medium">Media</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descrizione *</Label>
              <Textarea
                data-testid="ticket-description-input"
                placeholder="Descrivi il problema in dettaglio..."
                rows={5}
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button data-testid="submit-ticket-btn" onClick={handleCreate} className="bg-primary text-primary-foreground">
              Invia Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase Extra Tickets Dialog */}
      <Dialog open={showPurchase} onOpenChange={setShowPurchase}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Acquista Ticket Extra</DialogTitle>
            <DialogDescription>I ticket extra si aggiungono al tuo limite annuale e non scadono con il periodo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Quantita'</Label>
              <Input
                data-testid="extra-ticket-qty"
                type="number"
                min={1}
                max={100}
                value={purchaseQty}
                onChange={(e) => setPurchaseQty(Math.max(1, parseInt(e.target.value) || 1))}
              />
            </div>
            <div className="p-3 rounded-md bg-secondary/50 border border-border text-center">
              <p className="text-2xl font-bold text-primary">{(purchaseQty * ticketPrice).toFixed(2)} EUR</p>
              <p className="text-xs text-muted-foreground">{purchaseQty} ticket x {ticketPrice.toFixed(2)} EUR/ticket</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchase(false)}>Annulla</Button>
            <Button
              data-testid="confirm-purchase-extra"
              onClick={handlePurchaseExtra}
              disabled={purchasing}
              className="bg-primary text-primary-foreground"
            >
              {purchasing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Redirect a Stripe...
                </>
              ) : 'Procedi al Pagamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {verifyingPayment && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 flex items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span>Verifica pagamento in corso...</span>
          </div>
        </div>
      )}
    </div>
  );
}
